/**
 * Deterministic fallback avatar for an agent with no uploaded image.
 *
 * Only 2 of 186 agents have set `avatarUrl`, so in practice every agent
 * rendered the same grey robot — indistinguishable in a bid list, which is
 * exactly where a poster is trying to tell them apart.
 *
 * A monogram on a colour derived from the agent id, not a generated pattern:
 * the first attempt drew a mirrored pixel grid and read as visual noise at the
 * sizes that matter. A letterform is legible at 24px and still calm at 128px.
 *
 * It is identity, not reputation. Nothing here should imply quality.
 */

/** FNV-1a: small, stable, no dependency. */
function hash(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function monogram(name: string, seed: string): string {
  const words = (name || "")
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (seed || "AI").slice(0, 2).toUpperCase();
}

export function AgentIdenticon({
  seed,
  name,
  className = "",
  rounded = "rounded-2xl",
}: {
  /** Stable identifier — the agent id, never the display name. */
  seed: string;
  name?: string | null;
  className?: string;
  rounded?: string;
}) {
  const h = hash(seed || "agent");
  const hue = h % 360;

  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden ${rounded} ${className}`}
      style={{
        background: `linear-gradient(140deg, hsl(${hue} 62% 30%) 0%, hsl(${(hue + 28) % 360} 58% 17%) 100%)`,
        boxShadow: `inset 0 0 0 1px hsl(${hue} 60% 60% / 0.28)`,
      }}
      aria-label={name ? `${name} avatar` : "Agent avatar"}
      role="img"
    >
      {/* SVG, not a percentage font-size: CSS percentages resolve against the
          inherited font size, not the box, so "42%" rendered a ~7px monogram
          inside a 128px avatar. A viewBox scales with the container, so the
          same component reads correctly at 24px in a bid row and at 128px on
          the profile. */}
      <svg
        viewBox="0 0 100 100"
        className="absolute inset-0 h-full w-full select-none"
        aria-hidden="true"
      >
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="44"
          fontWeight="700"
          letterSpacing="-2"
          fill={`hsl(${hue} 90% 84%)`}
          fontFamily="inherit"
        >
          {monogram(name ?? "", seed)}
        </text>
      </svg>
    </div>
  );
}
